import type { Scene } from '@babylonjs/core/scene';

import { FACE, GEAR } from './atlas';
import type { ModelPalette } from './palette';
import {
  box,
  capsule,
  createRig,
  cylinder,
  lathe,
  type LimbLengths,
  type Rig,
  sphere,
  torus,
  wedge,
} from './rig';

/**
 * The monsters that are not skeletons.
 *
 * Every mob in the game was `buildSkeleton` regardless of what the catalogue said
 * it was, so a Rotting Ghoul, an Ancient Mummy and a Fire Imp were the same pile
 * of bones at three sizes. These are four more silhouettes, chosen to be told
 * apart **at the gameplay camera** — roughly 30 × 45 device pixels — which rules
 * out detail and leaves posture, proportion and mass.
 *
 * Each is deliberately extreme in one axis:
 *
 * | | Reads as |
 * |---|---|
 * | Ghoul | hunched forward, arms nearly to the floor, no weapon |
 * | Wrapped (mummy, wight) | tall, stiff, arms crossed high, wide shoulders |
 * | Caster (bone mage) | robed cone with no visible legs, one tall staff |
 * | Imp | tiny, wide-headed, horned, bandy-legged |
 * | Brute (dread knight, bosses) | massive shoulders, heavy weapon, thick legs |
 *
 * They share the rig and every pose function, so animation is free: the pose maths
 * knows about joints, not about species.
 *
 * **Phase 41 rebuilt each from rounded parts** — ellipsoid bodies, capsule limbs split at
 * the elbow and the knee, sphere heads — with the same silhouette rule each one already
 * had. The part counts are what they were, give or take one, because the count is the
 * draw calls and the draw calls are what a pack of thirty-two costs.
 */

/** A hunched carrion-eater. Arms long enough to drag. */
export function buildGhoul(scene: Scene, palette: ModelPalette): Rig {
  const limbs: LimbLengths = { upperArm: 0.32, foreArm: 0.34, thigh: 0.34, shin: 0.3 };
  const rig = createRig(scene, 'ghoul', 0.62, limbs, 0.3, 0.14, 0.42);
  const parts = rig.parts;
  const flesh = palette.get('rust');
  const dark = palette.get('boneDark');
  const bone = palette.get('bone');

  // A deep, forward-slumped chest. The lean is baked into the geometry rather
  // than the pose, so it survives every animation state.
  sphere(scene, rig.torso, parts, {
    name: 'gh-chest',
    size: [0.46, 0.44, 0.36],
    at: [0, 0.3, 0.08],
    rotation: [0.4, 0, 0],
    material: flesh,
    segments: 6,
  });
  sphere(scene, rig.torso, parts, {
    name: 'gh-hips',
    size: [0.32, 0.2, 0.26],
    at: [0, 0.04, 0],
    material: dark,
    segments: 4,
  });
  // A ridge of exposed spine along the hunch — the one detail that reads at range.
  for (let i = 0; i < 3; i++) {
    wedge(scene, rig.torso, parts, {
      name: `gh-spur${i}`,
      size: [0.07, 0.12, 0.06],
      taper: 0.2,
      at: [0, 0.24 + i * 0.13, -0.17 + i * 0.03],
      rotation: [-0.5, 0, 0],
      material: bone,
    });
  }

  sphere(scene, rig.head, parts, {
    name: 'gh-skull',
    size: [0.24, 0.22, 0.3],
    at: [0, 0, 0.06],
    // The skeletal jaw painted on dark cloth: a ghoul and a human cutthroat share this rig,
    // and a half-covered face is the one reading that suits both.
    material: palette.cell(FACE.jawSkeletal, 'bone'),
    segments: 6,
  });
  sphere(scene, rig.head, parts, {
    name: 'gh-jaw',
    size: [0.18, 0.08, 0.2],
    at: [0, -0.1, 0.12],
    material: dark,
    segments: 4,
  });

  // Arms to the knees, jointed, ending in claws. No weapon: a ghoul uses its hands, and an
  // empty hand is its own silhouette.
  jointedArms(scene, rig, limbs, {
    upper: 0.065,
    lower: 0.055,
    material: flesh,
    hand: null,
  });
  for (const [side, hand] of [
    ['R', rig.handR],
    ['L', rig.handL],
  ] as const) {
    wedge(scene, hand, parts, {
      name: `gh-claw${side}`,
      size: [0.16, 0.22, 0.08],
      taper: 0.25,
      flip: true,
      at: [0, -0.08, 0.05],
      rotation: [-0.6, 0, 0],
      material: bone,
    });
  }

  legs(scene, rig, limbs, { girth: 0.15, material: flesh, foot: dark });
  return rig;
}

/** Bound in grave wrappings: tall, stiff, arms crossed. Mummies and wights. */
export function buildWrapped(scene: Scene, palette: ModelPalette): Rig {
  const limbs: LimbLengths = { upperArm: 0.26, foreArm: 0.26, thigh: 0.4, shin: 0.36 };
  const rig = createRig(scene, 'wrapped', 0.94, limbs, 0.3, 0.14, 0.5);
  const parts = rig.parts;
  const wrap = palette.get('cloth');
  const dark = palette.get('boneDark');
  const ember = palette.get('ember');

  // Wide shoulders over a narrow waist — a wedge, which is the most legible
  // silhouette at distance of anything here.
  sphere(scene, rig.torso, parts, {
    name: 'wr-chest',
    size: [0.56, 0.5, 0.32],
    at: [0, 0.42, 0],
    material: wrap,
    segments: 6,
  });
  cylinder(scene, rig.torso, parts, {
    name: 'wr-waist',
    height: 0.3,
    diameterTop: 0.32,
    diameterBottom: 0.26,
    at: [0, 0.14, 0],
    material: dark,
    tessellation: 8,
  });
  // Bands across the chest: three darker straps, the "wrapping" read — hoops, at a slant.
  for (let i = 0; i < 3; i++) {
    torus(scene, rig.torso, parts, {
      name: `wr-band${i}`,
      diameter: 0.5 - i * 0.03,
      thickness: 0.045,
      at: [0, 0.3 + i * 0.12, 0],
      rotation: [0.1, 0, 0.12],
      material: dark,
      tessellation: 10,
    }).scaling.z = 0.6;
  }

  sphere(scene, rig.head, parts, {
    name: 'wr-head',
    size: [0.24, 0.3, 0.24],
    at: [0, 0.05, 0],
    // Stone-grey, so a wight is not another skeleton at distance. The ember eyes below stay:
    // this cell has eyes painted in it, but they are not *lit*, and lit is the difference
    // between a statue and a thing that has noticed you.
    material: palette.cell(FACE.skinGargoyle, 'cloth'),
    segments: 6,
  });
  // Two ember eyes. The only light on the model, and what makes it read as awake.
  for (const side of [-1, 1]) {
    sphere(scene, rig.head, parts, {
      name: `wr-eye${side}`,
      size: [0.05, 0.04, 0.04],
      at: [side * 0.06, 0.06, 0.11],
      material: ember,
      segments: 3,
    });
  }

  // Arms held stiff and high across the body: the rig's hold does it, so the pose still
  // owns the joints — the *rest* of the arm is crossed, and a swing swings from there.
  rig.hold.armRPitch = 0.9;
  rig.hold.armLPitch = 0.9;
  rig.hold.armRYaw = 0.5;
  rig.hold.armLYaw = -0.5;
  rig.hold.elbowR = 0.9;
  rig.hold.elbowL = 0.9;
  jointedArms(scene, rig, limbs, { upper: 0.075, lower: 0.065, material: wrap, hand: dark });

  legs(scene, rig, limbs, { girth: 0.16, material: wrap, foot: dark });
  return rig;
}

/** A robed caster: a cone with no legs and a staff taller than it is. */
export function buildCaster(scene: Scene, palette: ModelPalette): Rig {
  const limbs: LimbLengths = { upperArm: 0.22, foreArm: 0.22, thigh: 0.3, shin: 0.2 };
  const rig = createRig(scene, 'caster', 0.86, limbs, 0.3, 0.12, 0.5);
  const parts = rig.parts;
  const robe = palette.get('cloth');
  const dark = palette.get('boneDark');
  const bone = palette.get('bone');
  const ember = palette.get('ember');

  // The robe *is* the body: a wide skirt to the ground, so the model has no gait
  // at all. A caster that walked like a fighter would read as one.
  cylinder(scene, rig.torso, parts, {
    name: 'ca-robe',
    height: 0.92,
    diameterTop: 0.34,
    diameterBottom: 0.7,
    at: [0, 0.2, 0],
    material: robe,
    tessellation: 10,
  });
  torus(scene, rig.torso, parts, {
    name: 'ca-collar',
    diameter: 0.36,
    thickness: 0.09,
    at: [0, 0.64, 0],
    material: dark,
    tessellation: 10,
  });

  // A hood with nothing but a light inside it.
  lathe(scene, rig.head, parts, {
    name: 'ca-hood',
    profile: [
      [0.15, 0],
      [0.16, 0.08],
      [0.11, 0.2],
      [0.03, 0.32],
      [0, 0.36],
    ],
    at: [0, -0.1, -0.01],
    material: dark,
    tessellation: 8,
  });
  sphere(scene, rig.head, parts, {
    name: 'ca-glow',
    size: [0.1, 0.05, 0.05],
    at: [0, 0.0, 0.12],
    material: ember,
    segments: 3,
  });

  // One arm holds the staff; the other is tucked into the robe and barely there.
  rig.hold.armLPitch = 0.6;
  rig.hold.elbowL = 1.4;
  rig.hold.armLYaw = -0.4;
  jointedArms(scene, rig, limbs, { upper: 0.06, lower: 0.055, material: robe, hand: bone });

  // The staff, in the hand, upright: a knotted shaft and a lit stone.
  cylinder(scene, rig.weapon, rig.heldParts, {
    name: 'ca-staff',
    height: 1.5,
    diameterTop: 0.05,
    diameterBottom: 0.045,
    at: [0, 0.28, 0],
    material: bone,
    tessellation: 6,
  });
  lathe(scene, rig.weapon, rig.heldParts, {
    name: 'ca-staff-tip',
    profile: [
      [0.01, 0],
      [0.07, 0.08],
      [0.01, 0.2],
    ],
    at: [0, 0.98, 0],
    material: ember,
    tessellation: 6,
  });

  // Legs exist as joints so the pose functions have something to write to, but
  // they are inside the robe and barely visible.
  legs(scene, rig, limbs, { girth: 0.1, material: dark, foot: dark });
  return rig;
}

/** Small, wide-headed and horned. The one monster smaller than the player. */
export function buildImp(scene: Scene, palette: ModelPalette): Rig {
  const limbs: LimbLengths = { upperArm: 0.15, foreArm: 0.15, thigh: 0.2, shin: 0.16 };
  const rig = createRig(scene, 'imp', 0.44, limbs, 0.18, 0.1, 0.28);
  const parts = rig.parts;
  const hide = palette.get('rust');
  const dark = palette.get('boneDark');
  const ember = palette.get('ember');

  sphere(scene, rig.torso, parts, {
    name: 'im-belly',
    size: [0.3, 0.3, 0.28],
    at: [0, 0.16, 0],
    material: hide,
    segments: 6,
  });

  // A head nearly as wide as the body. Comically top-heavy, which is exactly what
  // makes something small read as small rather than as far away.
  sphere(scene, rig.head, parts, {
    name: 'im-head',
    size: [0.32, 0.26, 0.28],
    at: [0, 0.04, 0.02],
    // Green, and the only green thing in the game. An imp is the one monster smaller than
    // the player, so it needs a colour cue rather than a silhouette one.
    material: palette.cell(FACE.skinOrc, 'rust'),
    segments: 6,
  });
  for (const side of [-1, 1]) {
    wedge(scene, rig.head, parts, {
      name: `im-horn${side}`,
      size: [0.06, 0.2, 0.06],
      taper: 0.1,
      at: [side * 0.11, 0.18, -0.02],
      rotation: [0, 0, side * -0.45],
      material: dark,
    });
    sphere(scene, rig.head, parts, {
      name: `im-eye${side}`,
      size: [0.05, 0.04, 0.04],
      at: [side * 0.07, 0.04, 0.14],
      material: ember,
      segments: 3,
    });
  }

  jointedArms(scene, rig, limbs, { upper: 0.04, lower: 0.035, material: hide, hand: dark });
  legs(scene, rig, limbs, { girth: 0.11, material: hide, foot: dark });
  return rig;
}

/** Massive and armoured: the dread knight, and every boss until they get their own. */
export function buildBrute(scene: Scene, palette: ModelPalette): Rig {
  const limbs: LimbLengths = { upperArm: 0.28, foreArm: 0.28, thigh: 0.46, shin: 0.4 };
  const rig = createRig(scene, 'brute', 1.05, limbs, 0.4, 0.18, 0.5);
  const parts = rig.parts;
  const plate = palette.get('darkSteel');
  const rust = palette.get('rust');
  const ember = palette.get('ember');
  const steel = palette.get('steel');
  const plateCell = palette.cell(GEAR.ironPlate, 'darkSteel');

  sphere(scene, rig.torso, parts, {
    name: 'br-chest',
    size: [0.72, 0.6, 0.44],
    at: [0, 0.44, 0],
    material: plateCell,
    segments: 8,
  });
  torus(scene, rig.torso, parts, {
    name: 'br-belt',
    diameter: 0.46,
    thickness: 0.13,
    at: [0, 0.14, 0],
    material: rust,
    tessellation: 10,
  });
  // Pauldrons wider than the chest. The whole silhouette is these: domes, tipped outward.
  for (const side of [-1, 1]) {
    sphere(scene, rig.torso, parts, {
      name: `br-pauldron${side}`,
      size: [0.36, 0.34, 0.4],
      at: [side * 0.42, 0.58, 0],
      rotation: [0, 0, side * -0.4],
      material: plate,
      segments: 6,
      slice: 0.55,
    });
  }

  // A great helm: a turned dome with a flat crown and one burning slit.
  lathe(scene, rig.head, parts, {
    name: 'br-helm',
    profile: [
      [0.16, 0],
      [0.17, 0.12],
      [0.16, 0.24],
      [0.1, 0.3],
      [0, 0.31],
    ],
    at: [0, -0.1, 0],
    // A gear cell, not a face one — this rig is a helm with an ember slit already, so what it
    // wants is metal, and the iron visor cell would put a second grate over the one below.
    material: plateCell,
    tessellation: 10,
  });
  box(scene, rig.head, parts, {
    name: 'br-visor',
    size: [0.2, 0.05, 0.04],
    at: [0, 0.03, 0.15],
    material: ember,
  });

  jointedArms(scene, rig, limbs, { upper: 0.1, lower: 0.09, material: plate, hand: rust });

  // A greatsword: long, straight, unmistakably heavy. Into rig.heldParts — the
  // collider is measured from the body, and this blade reaches a unit above the head.
  wedge(scene, rig.weapon, rig.heldParts, {
    name: 'br-blade',
    size: [0.14, 1.35, 0.05],
    taper: 0.15,
    at: [0, 0.6, 0],
    material: steel,
  });
  capsule(scene, rig.weapon, rig.heldParts, {
    name: 'br-guard',
    height: 0.42,
    radiusTop: 0.04,
    radiusBottom: 0.04,
    at: [0, 0.02, 0],
    rotation: [0, 0, Math.PI / 2],
    material: rust,
    tessellation: 6,
  });

  legs(scene, rig, limbs, { girth: 0.22, material: plate, foot: rust });
  return rig;
}

/**
 * The arm pair every model shares: an upper and a lower capsule on the rig's own joints,
 * and a fist at the end of each unless `hand` is null.
 */
function jointedArms(
  scene: Scene,
  rig: Rig,
  limbs: LimbLengths,
  spec: {
    upper: number;
    lower: number;
    material: ReturnType<ModelPalette['get']>;
    hand: ReturnType<ModelPalette['get']> | null;
  },
): void {
  for (const [side, arm, fore, hand] of [
    ['R', rig.armR, rig.foreR, rig.handR],
    ['L', rig.armL, rig.foreL, rig.handL],
  ] as const) {
    capsule(scene, arm, rig.parts, {
      name: `arm-upper${side}`,
      height: limbs.upperArm + spec.upper,
      radiusTop: spec.upper,
      radiusBottom: (spec.upper + spec.lower) / 2,
      at: [0, -limbs.upperArm / 2, 0],
      material: spec.material,
      tessellation: 6,
    });
    capsule(scene, fore, rig.parts, {
      name: `arm-lower${side}`,
      height: limbs.foreArm + spec.lower,
      radiusTop: (spec.upper + spec.lower) / 2,
      radiusBottom: spec.lower,
      at: [0, -limbs.foreArm / 2, 0],
      material: spec.material,
      tessellation: 6,
    });
    if (spec.hand) {
      sphere(scene, hand, rig.parts, {
        name: `arm-hand${side}`,
        size: [spec.lower * 2.2, spec.lower * 2.4, spec.lower * 2.2],
        at: [0, -0.01, 0.01],
        material: spec.hand,
        segments: 4,
      });
    }
  }
}

/** The leg pair every model shares, at whatever proportions it wants. */
function legs(
  scene: Scene,
  rig: Rig,
  limbs: LimbLengths,
  spec: {
    girth: number;
    material: ReturnType<ModelPalette['get']>;
    foot: ReturnType<ModelPalette['get']>;
  },
): void {
  for (const [side, leg, shin] of [
    ['R', rig.legR, rig.shinR],
    ['L', rig.legL, rig.shinL],
  ] as const) {
    capsule(scene, leg, rig.parts, {
      name: `leg-thigh${side}`,
      height: limbs.thigh + spec.girth * 0.4,
      radiusTop: spec.girth / 2,
      radiusBottom: spec.girth * 0.42,
      at: [0, -limbs.thigh / 2, 0],
      material: spec.material,
      tessellation: 6,
    });
    capsule(scene, shin, rig.parts, {
      name: `leg-shin${side}`,
      height: limbs.shin,
      radiusTop: spec.girth * 0.42,
      radiusBottom: spec.girth * 0.35,
      at: [0, -limbs.shin / 2 + 0.02, 0],
      material: spec.material,
      tessellation: 6,
    });
    capsule(scene, shin, rig.parts, {
      name: `leg-foot${side}`,
      height: spec.girth * 1.8,
      radiusTop: spec.girth * 0.4,
      radiusBottom: spec.girth * 0.4,
      at: [0, -limbs.shin + spec.girth * 0.3, spec.girth * 0.5],
      rotation: [Math.PI / 2, 0, 0],
      material: spec.foot,
      tessellation: 6,
    });
  }
}
